import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

// Types
interface User {
  email: string
  username: string
  isAdmin: boolean
  isMember: boolean
  guid: string
  locale?: string | null
}

interface LoginCredentials {
  email: string
  password: string
}

interface LoginResponse {
  success: boolean
  user?: User
  message?: string
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const isInitialized = ref(false)
  const isLoading = ref(false)
  const isInitializing = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(() => user.value?.isAdmin ?? false)
  const isMember = computed(() => user.value?.isMember ?? false)

  const login = async (
    credentials: LoginCredentials
  ): Promise<LoginResponse> => {
    isLoading.value = true

    try {
      const response = await fetch('/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
        body: new URLSearchParams({
          email: credentials.email,
          password: credentials.password,
        }),
        credentials: 'include',
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Server returns user data directly in login response
        user.value = data.user

        // Sync localStorage locale to server if it exists and differs from user's saved locale
        try {
          const localStorageLocale = localStorage.getItem('beerview_locale')
          if (localStorageLocale && localStorageLocale !== data.user?.locale) {
            // Send locale to server (fire and forget, don't block login)
            fetch('/api/users/me/locale', {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
              body: JSON.stringify({ locale: localStorageLocale }),
            }).catch(err =>
              console.warn('Failed to sync locale to server:', err)
            )
          }
        } catch (e) {
          console.warn('Failed to read or sync locale from localStorage:', e)
        }

        return { success: true, user: data.user }
      } else {
        return {
          success: false,
          message: data.message || 'Invalid email or password',
        }
      }
    } catch (error) {
      console.error('Login error:', error)
      return {
        success: false,
        message: 'Login failed. Please try again.',
      }
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    try {
      await fetch('/logout', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        credentials: 'include',
      })
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Clear user data regardless of API call success
      user.value = null

      // Redirect to home page
      window.location.href = '/'
    }
  }

  const updateUser = (newUserData: Partial<User>) => {
    if (user.value) {
      user.value = { ...user.value, ...newUserData }
    }
  }

  const initializeAuth = async () => {
    if (isInitialized.value || isInitializing.value) return

    isInitializing.value = true

    try {
      // Check for initial auth data from server
      const initialAuth = (window as any).__INITIAL_AUTH__
      if (initialAuth && typeof initialAuth === 'object') {
        if (initialAuth.authenticated && initialAuth.user) {
          user.value = initialAuth.user
        }
        isInitialized.value = true
        isInitializing.value = false
        return
      }

      // Fallback to API call if no initial data
      const response = await fetch('/api/session', {
        credentials: 'include',
      })

      if (response.ok) {
        const data = await response.json()
        if (data.authenticated && data.user) {
          user.value = data.user
        }
      }
    } catch (error) {
      console.error('Session check failed:', error)
    } finally {
      isInitialized.value = true
      isInitializing.value = false
    }
  }

  return {
    // State
    user,
    isInitialized,
    isLoading,
    isInitializing,

    // Getters
    isAuthenticated,
    isAdmin,
    isMember,

    // Actions
    login,
    logout,
    updateUser,
    initializeAuth,
  }
})
